import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Coffee, Undo2, History, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function fmtTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

export default function RedeemAtCounter({ client }) {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null); // null = haven't searched yet
  const [busyId, setBusyId] = useState(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (value) => {
    const needle = (value || "").trim();
    if (needle.length < 2) {
      setResults(null);
      return;
    }
    setSearching(true);
    try {
      const res = await client.get("/admin/passes/lookup", { params: { q: needle } });
      setResults(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed.");
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [client]);

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 250);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runSearch(q);
  };

  const updateRow = (updated) => {
    setResults((prev) => (prev || []).map((p) => (p.id === updated.id ? updated : p)));
  };

  const redeem = async (id) => {
    setBusyId(id);
    try {
      const res = await client.post(`/admin/passes/${id}/redeem`);
      updateRow(res.data);
      toast.success(`Bowl redeemed · ${res.data.bowls_remaining} of ${res.data.bowls_total} left`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not redeem.");
    } finally {
      setBusyId(null);
    }
  };

  const undo = async (id) => {
    setBusyId(id);
    try {
      const res = await client.post(`/admin/passes/${id}/redeem/undo`);
      updateRow(res.data);
      toast.success("Last redemption undone.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nothing to undo.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="redeem-tab">
      <form onSubmit={onSubmit} className="rounded-2xl bg-white border border-border p-5 md:p-6">
        <label htmlFor="redeem-search" className="block text-[10px] uppercase tracking-[0.2em] text-[#5C7365]">
          Look up a pass
        </label>
        <div className="mt-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5C7365]" aria-hidden />
            <Input
              id="redeem-search"
              value={q}
              onChange={onChange}
              data-testid="redeem-search-input"
              autoFocus
              autoComplete="off"
              inputMode="search"
              placeholder="Search by email, phone, or name…"
              className="h-12 md:h-14 pl-11 rounded-xl bg-[#F7F5F0] text-base"
            />
          </div>
          <Button
            type="submit"
            data-testid="redeem-search-btn"
            className="h-12 md:h-14 px-5 rounded-xl bg-primary text-primary-foreground hover:bg-[#1F382B] text-xs tracking-[0.18em] uppercase"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </Button>
        </div>
        <p className="mt-3 text-xs text-[#5C7365]">
          Only verified passes show up here. Tap once to deduct a bowl at the counter.
        </p>
      </form>

      <div className="mt-5 space-y-4">
        {results === null && (
          <div className="rounded-2xl bg-[#EBEFDE]/40 border border-dashed border-[#DDE3D5] p-8 text-center text-sm text-[#5C7365]" data-testid="redeem-empty-state">
            Start typing to find a pass.
          </div>
        )}
        {results && results.length === 0 && !searching && (
          <div className="rounded-2xl bg-white border border-border p-8 text-center text-sm text-[#5C7365] flex items-center justify-center gap-2" data-testid="redeem-no-results">
            <AlertCircle className="w-4 h-4" /> No verified pass matches “{q}”.
          </div>
        )}
        {results && results.map((p) => (
          <PassCard
            key={p.id}
            pass={p}
            busy={busyId === p.id}
            onRedeem={() => redeem(p.id)}
            onUndo={() => undo(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PassCard({ pass, busy, onRedeem, onUndo }) {
  const empty = pass.bowls_remaining <= 0;
  return (
    <div
      className="rounded-2xl bg-white border border-border p-5 md:p-6"
      data-testid={`redeem-card-${pass.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="font-serif text-2xl text-[#1A2B22]" data-testid={`redeem-name-${pass.id}`}>
            {pass.name}
          </div>
          <div className="text-xs text-[#5C7365] mt-1">
            {pass.email} · {pass.phone}
          </div>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#EBEFDE] text-[#2C4C3B] px-3 py-1 text-[11px] tracking-[0.16em] uppercase">
            {pass.tier_label}
          </div>
        </div>

        <div className="text-left md:text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C7365]">Bowls remaining</div>
          <div
            className="font-serif text-5xl md:text-6xl text-[#1A2B22] leading-none mt-1"
            data-testid={`redeem-remaining-${pass.id}`}
          >
            {pass.bowls_remaining}
            <span className="font-serif text-2xl text-[#5C7365]"> / {pass.bowls_total}</span>
          </div>
          {pass.last_redeemed_at && (
            <div className="mt-2 text-xs text-[#5C7365] inline-flex items-center gap-1">
              <History className="w-3 h-3" /> Last redeemed {fmtTime(pass.last_redeemed_at)}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-col-reverse md:flex-row md:items-center gap-3">
        <Button
          variant="ghost"
          onClick={onUndo}
          disabled={busy || pass.bowls_redeemed <= 0}
          data-testid={`redeem-undo-${pass.id}`}
          className="rounded-full h-10 px-4 text-xs tracking-[0.18em] uppercase text-[#5C7365] hover:text-[#1A2B22] disabled:opacity-40"
        >
          <Undo2 className="w-4 h-4 mr-2" /> Undo last
        </Button>
        <Button
          onClick={onRedeem}
          disabled={busy || empty}
          data-testid={`redeem-btn-${pass.id}`}
          className="md:ml-auto h-14 md:h-16 px-8 rounded-full bg-primary text-primary-foreground hover:bg-[#1F382B] text-sm tracking-[0.2em] uppercase disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Working…</>
          ) : empty ? (
            "All bowls redeemed"
          ) : (
            <><Coffee className="w-5 h-5 mr-2" /> Redeem 1 bowl</>
          )}
        </Button>
      </div>
    </div>
  );
}
